/**
 * Shared status types for the entire Go Sit Back workspace
 * This ensures consistency across admin and customer applications
 */

/**
 * Represents the possible statuses for a request in the system.
 *
 * @property PENDING   - The request is awaiting assessment.
 * @property ASSESSED  - The request has been assessed.
 * @property QUOTED    - A quote has been provided for the request.
 * @property EXPIRED   - The request or quote has expired.
 * @property CANCELLED - The request has been cancelled.
 */
export const REQUEST_STATUS = {
  PENDING: 'pending',
  ASSESSED: 'assessed', 
  QUOTED: 'quoted',
  EXPIRED: 'expired',
  CANCELLED: 'cancelled'
} as const;

/**
 * Represents the possible values of the `REQUEST_STATUS` object.
 * 
 * This type is a union of all values defined in the `REQUEST_STATUS` constant.
 * It is typically used to strongly type the status of a request throughout the application.
 *
 * @see REQUEST_STATUS
 */
export type RequestStatus = typeof REQUEST_STATUS[keyof typeof REQUEST_STATUS];

/**
 * Represents the possible statuses for a job in the system.
 * 
 * @property {'pending'} PENDING - The job is awaiting action or approval.
 * @property {'requested'} REQUESTED - The job has been requested but not yet confirmed.
 * @property {'confirmed'} CONFIRMED - The job has been confirmed and is scheduled to proceed.
 * @property {'in-progress'} IN_PROGRESS - The job is currently being worked on.
 * @property {'completed'} COMPLETED - The job has been finished.
 * @property {'cancelled'} CANCELLED - The job has been cancelled and will not proceed.
 * @property {'en-route'} EN_ROUTE - The service provider is on the way to the job location.
 * @property {'invoiced'} INVOICED - The job has been completed and invoiced.
 */
export const JOB_STATUS = {
  PENDING: 'pending',
  REQUESTED: 'requested',
  CONFIRMED: 'confirmed',
  IN_PROGRESS: 'in-progress', 
  COMPLETED: 'completed',
  CANCELLED: 'cancelled',
  EN_ROUTE: 'en-route',
  INVOICED: 'invoiced'
} as const;

/**
 * Represents the possible status values for a job, derived from the values of the `JOB_STATUS` object.
 * 
 * This type is a union of all values defined in `JOB_STATUS`.
 * 
 * @see JOB_STATUS
 */
export type JobStatus = typeof JOB_STATUS[keyof typeof JOB_STATUS];

/**
 * Represents the possible statuses for an invoice.
 *
 * @remarks
 * This constant object defines the allowed string values for invoice statuses.
 * It is marked as `const` to ensure the values are immutable and can be used for type inference.
 *
 * @example
 * ```typescript
 * if (invoice.status === INVOICE_STATUS.PAID) {
 *   // Handle paid invoice
 * }
 * ```
 *
 * @property DRAFT - The invoice is in draft state and not finalized.
 * @property FINALIZED - The invoice has been finalized but not yet sent.
 * @property SENT - The invoice has been sent to the recipient.
 * @property PAID - The invoice has been paid.
 * @property PENDING - The invoice is pending and awaiting action.
 * @property OVERDUE - The invoice is overdue for payment.
 */
export const INVOICE_STATUS = {
  DRAFT: 'draft',
  FINALIZED: 'finalized',
  SENT: 'sent', 
  PAID: 'paid',
  PENDING: 'pending',
  OVERDUE: 'overdue'
} as const;

/**
 * Represents the possible status values for an invoice.
 * 
 * This type is derived from the values of the `INVOICE_STATUS` object,
 * ensuring that only valid invoice status strings are allowed.
 *
 * @see INVOICE_STATUS
 */
export type InvoiceStatus = typeof INVOICE_STATUS[keyof typeof INVOICE_STATUS];

/**
 * Represents the possible statuses for a quote in the system.
 * 
 * @property DRAFT - This is a quote that has not been sent to the client yet, and is only visible to admins and managers. Once a quote has been sent (or marked awaiting response) it is not possible to put the quote back into draft status. 
 * @property AWAITING_RESPONSE - This is a quote that has been sent to the client and is awaiting approval or a change request. Awaiting response quotes are read-only and cannot be modified.
 * @property APPROVED - This is a quote that has been approved by the client (quotes are automatically approved if the customer submitted a request online), or has been marked as approved manually.
 * @property CHANGES_REQUESTED - This is a quote that a client is requesting changes on.
 * @property PENDING_ASSIGNMENT - This is a quote that has been broadcast to businesses, but has not been accepted by any business yet.
 * @property EXCLUSIVE_PENDING - This is a quote that has been exclusively offered to a specific business for 24 hours. After expiration, it can be broadcast to other businesses.
 * @property ASSIGNED - This is a quote that has been accepted by a business and is now assigned to them. This is a final status for a quote.
 * @property ARCHIVED - After the job associated with a quote has been completed, the quote is automatically archived. Archived quotes are read-only and cannot be modified.
 */
export const QUOTE_STATUS = {
  DRAFT: 'draft',
  AWAITING_RESPONSE: 'awaiting_response',
  APPROVED: 'approved',
  CHANGES_REQUESTED: 'changes_requested',
  PENDING_ASSIGNMENT: 'pending_assignment',
  EXCLUSIVE_PENDING: 'exclusive_pending',
  ASSIGNED: 'assigned',
  ARCHIVED: 'archived'
} as const;

/**
 * Represents the possible status values for a quote.
 * 
 * This type is derived from the values of the `QUOTE_STATUS` object.
 * It ensures that only valid quote status values, as defined in `QUOTE_STATUS`, can be used.
 */
export type QuoteStatus = typeof QUOTE_STATUS[keyof typeof QUOTE_STATUS];

/**
 * Represents the possible types of events that can occur for a job.
 * 
 * @property {string} CREATED - Indicates a job was created.
 * @property {string} UPDATED - Indicates a job was updated.
 * @property {string} STATUS_CHANGED - Indicates the status of a job changed.
 * @property {string} EMAIL_SENT - Indicates an email related to the job was sent.
 * @property {string} EMPLOYEE_ASSIGNED - Indicates an employee was assigned to the job.
 * @property {string} CUSTOMER_CONTACTED - Indicates the customer was contacted regarding the job.
 * @property {string} NOTE_ADDED - Indicates a note was added to the job.
 * @property {string} CANCELLED - Indicates the job was cancelled.
 * @property {string} INVOICE_GENERATED - Indicates an invoice was generated for the job.
 * @property {string} ERROR - Indicates an error occurred related to the job.
 */
export const JOB_EVENT_TYPE = {
  CREATED: 'created',
  UPDATED: 'updated', 
  STATUS_CHANGED: 'status_changed',
  EMAIL_SENT: 'email_sent',
  EMPLOYEE_ASSIGNED: 'employee_assigned',
  CUSTOMER_CONTACTED: 'customer_contacted',
  NOTE_ADDED: 'note_added',
  CANCELLED: 'cancelled',
  INVOICE_GENERATED: 'invoice_generated', 
  ERROR: 'error'
} as const;

/**
 * Represents the possible event types for a job, as defined by the keys of the `JOB_EVENT_TYPE` object.
 * 
 * This type is a union of all values in the `JOB_EVENT_TYPE` constant, allowing for type-safe usage of job event types.
 *
 * @see JOB_EVENT_TYPE
 */
export type JobEventType = typeof JOB_EVENT_TYPE[keyof typeof JOB_EVENT_TYPE];

// Note: Business-related constants (URGENCY_LEVEL, EMPLOYEE_STATUS, SERVICE_CATEGORY, 
// BUSINESS_STATUS, PROPERTY_TYPE) have been moved to business.ts to avoid duplication.
// Import them from business.ts when needed.

/**
 * Returns a color string corresponding to the given status and type.
 *
 * The color is determined based on the provided `type` ('job', 'invoice', 'quote', or 'request')
 * and the specific `status` value for that type. If the status does not match any known value,
 * 'gray' is returned as the default color.
 *
 * @param status - The status string to evaluate. Should correspond to a value from the appropriate status enum for the given type.
 * @param type - The type of entity ('job', 'invoice', 'quote', or 'request') for which the status color is being determined.
 * @returns The color string associated with the given status and type.
 */
export const getStatusColor = (status: string, type: 'job' | 'invoice' | 'quote' | 'request'): string => {
  switch (type) {
    case 'job':
      switch (status) {
        case JOB_STATUS.PENDING: return 'gray';
        case JOB_STATUS.REQUESTED: return 'blue';
        case JOB_STATUS.CONFIRMED: return 'green';
        case JOB_STATUS.IN_PROGRESS: return 'yellow';
        case JOB_STATUS.COMPLETED: return 'emerald';
        case JOB_STATUS.CANCELLED: return 'red';
        case JOB_STATUS.EN_ROUTE: return 'purple';
        case JOB_STATUS.INVOICED: return 'indigo';
        default: return 'gray';
      }
    case 'invoice':
      switch (status) {
        case INVOICE_STATUS.DRAFT: return 'gray';
        case INVOICE_STATUS.FINALIZED: return 'blue';
        case INVOICE_STATUS.SENT: return 'yellow';
        case INVOICE_STATUS.PAID: return 'green';
        case INVOICE_STATUS.PENDING: return 'orange';
        case INVOICE_STATUS.OVERDUE: return 'red';
        default: return 'gray';
      }
    case 'quote':
      switch (status) {
        case QUOTE_STATUS.DRAFT: return 'gray';
        case QUOTE_STATUS.AWAITING_RESPONSE: return 'blue';
        case QUOTE_STATUS.APPROVED: return 'purple';
        case QUOTE_STATUS.CHANGES_REQUESTED: return 'yellow';
        case QUOTE_STATUS.PENDING_ASSIGNMENT: return 'orange';
        case QUOTE_STATUS.EXCLUSIVE_PENDING: return 'indigo';
        case QUOTE_STATUS.ASSIGNED: return 'green';
        case QUOTE_STATUS.ARCHIVED: return 'red';
        default: return 'gray';
      }
    case 'request':
      switch (status) {
        case REQUEST_STATUS.PENDING: return 'yellow';
        case REQUEST_STATUS.ASSESSED: return 'blue';
        case REQUEST_STATUS.QUOTED: return 'purple';
        case REQUEST_STATUS.EXPIRED: return 'orange';
        case REQUEST_STATUS.CANCELLED: return 'red';
        default: return 'gray';
      }
    default:
      return 'gray';
  }
};

/**
 * Returns a human-readable label for a given status and type.
 *
 * @param status - The status code or identifier to be converted to a label.
 * @param type - The type of entity the status belongs to. Can be 'job', 'invoice', 'quote', or 'request'.
 * @returns The corresponding label for the provided status and type. If the status is not recognized, returns the original status string.
 *
 * @example
 * ```typescript
 * getStatusLabel(JOB_STATUS.COMPLETED, 'job'); // "Completed"
 * getStatusLabel(INVOICE_STATUS.PAID, 'invoice'); // "Paid"
 * ```
 */
export const getStatusLabel = (status: string, type: 'job' | 'invoice' | 'quote' | 'request'): string => {
  switch (type) {
    case 'job':
      switch (status) {
        case JOB_STATUS.PENDING: return 'Pending';
        case JOB_STATUS.REQUESTED: return 'Requested';
        case JOB_STATUS.CONFIRMED: return 'Confirmed';
        case JOB_STATUS.IN_PROGRESS: return 'In Progress';
        case JOB_STATUS.COMPLETED: return 'Completed';
        case JOB_STATUS.CANCELLED: return 'Cancelled';
        case JOB_STATUS.EN_ROUTE: return 'En Route';
        case JOB_STATUS.INVOICED: return 'Invoiced';
        default: return status;
      }
    case 'invoice':
      switch (status) {
        case INVOICE_STATUS.DRAFT: return 'Draft';
        case INVOICE_STATUS.FINALIZED: return 'Finalized';
        case INVOICE_STATUS.SENT: return 'Sent';
        case INVOICE_STATUS.PAID: return 'Paid';
        case INVOICE_STATUS.PENDING: return 'Pending';
        case INVOICE_STATUS.OVERDUE: return 'Overdue';
        default: return status;
      }
    case 'quote':
      switch (status) {
        case QUOTE_STATUS.DRAFT: return 'Draft';
        case QUOTE_STATUS.AWAITING_RESPONSE: return 'Awaiting Response';
        case QUOTE_STATUS.APPROVED: return 'Approved';
        case QUOTE_STATUS.CHANGES_REQUESTED: return 'Changes Requested';
        case QUOTE_STATUS.PENDING_ASSIGNMENT: return 'Pending Assignment';
        case QUOTE_STATUS.EXCLUSIVE_PENDING: return 'Exclusive Pending';
        case QUOTE_STATUS.ASSIGNED: return 'Assigned';
        case QUOTE_STATUS.ARCHIVED: return 'Archived';
        default: return status;
      }
    case 'request':
      switch (status) {
        case REQUEST_STATUS.PENDING: return 'Pending';
        case REQUEST_STATUS.ASSESSED: return 'Assessed';
        case REQUEST_STATUS.QUOTED: return 'Quoted';
        case REQUEST_STATUS.EXPIRED: return 'Expired';
        case REQUEST_STATUS.CANCELLED: return 'Cancelled';
        default: return status;
      }
    default:
      return status;
  }
};

/**
 * Determines if a status transition is valid for a given entity type.
 *
 * @param currentStatus - The current status of the entity (e.g., job, invoice, quote, or request).
 * @param newStatus - The desired new status to transition to.
 * @param type - The type of entity for which the status transition is being checked. Can be 'job', 'invoice', 'quote', or 'request'.
 * @returns `true` if the transition from `currentStatus` to `newStatus` is valid for the specified `type`, otherwise `false`.
 *
 * @example
 * ```typescript
 * isValidStatusTransition('REQUESTED', 'CONFIRMED', 'job'); // true
 * isValidStatusTransition('DRAFT', 'FINALIZED', 'invoice'); // true
 * isValidStatusTransition('SENT', 'PAID', 'invoice'); // false
 * ```
 */
export const isValidStatusTransition = (
  currentStatus: string, 
  newStatus: string, 
  type: 'job' | 'invoice' | 'quote' | 'request'
): boolean => {
  const transitions: Record<string, Record<string, string[]>> = {
    job: {
      [JOB_STATUS.REQUESTED]: [JOB_STATUS.CONFIRMED, JOB_STATUS.CANCELLED],
      [JOB_STATUS.CONFIRMED]: [JOB_STATUS.EN_ROUTE, JOB_STATUS.IN_PROGRESS, JOB_STATUS.CANCELLED],
      [JOB_STATUS.EN_ROUTE]: [JOB_STATUS.IN_PROGRESS, JOB_STATUS.CANCELLED],
      [JOB_STATUS.IN_PROGRESS]: [JOB_STATUS.COMPLETED, JOB_STATUS.CANCELLED],
      [JOB_STATUS.COMPLETED]: [JOB_STATUS.INVOICED],
      [JOB_STATUS.CANCELLED]: [],
      [JOB_STATUS.INVOICED]: []
    },
    invoice: {
      [INVOICE_STATUS.DRAFT]: [INVOICE_STATUS.FINALIZED],
      [INVOICE_STATUS.FINALIZED]: [INVOICE_STATUS.SENT],
      [INVOICE_STATUS.SENT]: [INVOICE_STATUS.PAID, INVOICE_STATUS.OVERDUE],
      [INVOICE_STATUS.PENDING]: [INVOICE_STATUS.PAID, INVOICE_STATUS.OVERDUE],
      [INVOICE_STATUS.OVERDUE]: [INVOICE_STATUS.PAID],
      [INVOICE_STATUS.PAID]: []
    },
    quote: {
      [QUOTE_STATUS.DRAFT]: [QUOTE_STATUS.AWAITING_RESPONSE, QUOTE_STATUS.ARCHIVED],
      [QUOTE_STATUS.AWAITING_RESPONSE]: [QUOTE_STATUS.APPROVED, QUOTE_STATUS.CHANGES_REQUESTED, QUOTE_STATUS.ARCHIVED],
      [QUOTE_STATUS.APPROVED]: [QUOTE_STATUS.PENDING_ASSIGNMENT, QUOTE_STATUS.EXCLUSIVE_PENDING, QUOTE_STATUS.ARCHIVED],
      [QUOTE_STATUS.CHANGES_REQUESTED]: [QUOTE_STATUS.AWAITING_RESPONSE, QUOTE_STATUS.ARCHIVED],
      [QUOTE_STATUS.PENDING_ASSIGNMENT]: [QUOTE_STATUS.ASSIGNED, QUOTE_STATUS.ARCHIVED],
      [QUOTE_STATUS.EXCLUSIVE_PENDING]: [QUOTE_STATUS.ASSIGNED, QUOTE_STATUS.PENDING_ASSIGNMENT, QUOTE_STATUS.ARCHIVED],
      [QUOTE_STATUS.ASSIGNED]: [QUOTE_STATUS.ARCHIVED],
      [QUOTE_STATUS.ARCHIVED]: []
    },
    request: {
      [REQUEST_STATUS.PENDING]: [REQUEST_STATUS.ASSESSED, REQUEST_STATUS.CANCELLED],
      [REQUEST_STATUS.ASSESSED]: [REQUEST_STATUS.QUOTED, REQUEST_STATUS.CANCELLED],
      [REQUEST_STATUS.QUOTED]: [REQUEST_STATUS.EXPIRED, REQUEST_STATUS.CANCELLED],
      [REQUEST_STATUS.EXPIRED]: [REQUEST_STATUS.QUOTED, REQUEST_STATUS.CANCELLED],
      [REQUEST_STATUS.CANCELLED]: []
    }
  };

  const validTransitions = transitions[type]?.[currentStatus] || [];
  return validTransitions.includes(newStatus);
};

/**
 * Returns a color string corresponding to the given job status.
 *
 * @param status - The current status of the job.
 * @returns A string representing the color associated with the provided job status.
 *
 * | Job Status         | Color    |
 * |--------------------|----------|
 * | REQUESTED          | blue     |
 * | CONFIRMED          | green    |
 * | IN_PROGRESS        | yellow   |
 * | COMPLETED          | emerald  |
 * | CANCELLED          | red      |
 * | EN_ROUTE           | purple   |
 * | INVOICED           | indigo   |
 * | (default/unknown)  | gray     |
 */
export const getJobStatusColor = (status: JobStatus): string => {
  switch (status) {
    case JOB_STATUS.PENDING: return 'gray';
    case JOB_STATUS.REQUESTED: return 'blue';
    case JOB_STATUS.CONFIRMED: return 'green';
    case JOB_STATUS.IN_PROGRESS: return 'yellow';
    case JOB_STATUS.COMPLETED: return 'emerald';
    case JOB_STATUS.CANCELLED: return 'red';
    case JOB_STATUS.EN_ROUTE: return 'purple';
    case JOB_STATUS.INVOICED: return 'indigo';
    default: return 'gray';
  }
};

/**
 * Returns a human-readable label for a given job status.
 *
 * @param status - The job status to convert to a label.
 * @returns The corresponding label as a string. If the status is not recognized, returns the status itself.
 */
export const getJobStatusLabel = (status: JobStatus): string => {
  switch (status) {
    case JOB_STATUS.PENDING: return 'Pending';
    case JOB_STATUS.REQUESTED: return 'Requested';
    case JOB_STATUS.CONFIRMED: return 'Confirmed';
    case JOB_STATUS.IN_PROGRESS: return 'In Progress';
    case JOB_STATUS.COMPLETED: return 'Completed';
    case JOB_STATUS.CANCELLED: return 'Cancelled';
    case JOB_STATUS.EN_ROUTE: return 'En Route';
    case JOB_STATUS.INVOICED: return 'Invoiced';
    default: return status;
  }
};

/**
 * Returns the color associated with a given request status.
 *
 * @param status - The status of the request.
 * @returns The color string corresponding to the provided request status.
 */
export const getRequestStatusColor = (status: RequestStatus): string => {
  switch (status) {
    case REQUEST_STATUS.PENDING: return 'yellow';
    case REQUEST_STATUS.ASSESSED: return 'blue';
    case REQUEST_STATUS.QUOTED: return 'purple';
    case REQUEST_STATUS.EXPIRED: return 'orange';
    case REQUEST_STATUS.CANCELLED: return 'red';
    default: return 'gray';
  }
};

/**
 * Returns a human-readable label for a given request status.
 *
 * @param status - The status of the request, represented as a `RequestStatus` enum value.
 * @returns The corresponding label as a string. If the status does not match any known value,
 * returns the status itself.
 */
export const getRequestStatusLabel = (status: RequestStatus): string => {
  switch (status) {
    case REQUEST_STATUS.PENDING: return 'Pending';
    case REQUEST_STATUS.ASSESSED: return 'Assessed';
    case REQUEST_STATUS.QUOTED: return 'Quoted';
    case REQUEST_STATUS.EXPIRED: return 'Expired';
    case REQUEST_STATUS.CANCELLED: return 'Cancelled';
    default: return status;
  }
};

/**
 * Returns a color string corresponding to the given invoice status.
 *
 * @param status - The status of the invoice.
 * @returns The color associated with the provided invoice status.
 *
 * | Status                  | Color   |
 * |-------------------------|---------|
 * | INVOICE_STATUS.DRAFT    | 'gray'  |
 * | INVOICE_STATUS.FINALIZED| 'blue'  |
 * | INVOICE_STATUS.SENT     | 'yellow'|
 * | INVOICE_STATUS.PAID     | 'green' |
 * | INVOICE_STATUS.PENDING  | 'orange'|
 * | INVOICE_STATUS.OVERDUE  | 'red'   |
 * | (default)               | 'gray'  |
 */
export const getInvoiceStatusColor = (status: InvoiceStatus): string => {
  switch (status) {
    case INVOICE_STATUS.DRAFT: return 'gray';
    case INVOICE_STATUS.FINALIZED: return 'blue';
    case INVOICE_STATUS.SENT: return 'yellow';
    case INVOICE_STATUS.PAID: return 'green';
    case INVOICE_STATUS.PENDING: return 'orange';
    case INVOICE_STATUS.OVERDUE: return 'red';
    default: return 'gray';
  }
};

/**
 * Returns a human-readable label for a given invoice status.
 *
 * @param status - The invoice status to get the label for.
 * @returns The corresponding label as a string. If the status is not recognized, returns the status itself.
 */
export const getInvoiceStatusLabel = (status: InvoiceStatus): string => {
  switch (status) {
    case INVOICE_STATUS.DRAFT: return 'Draft';
    case INVOICE_STATUS.FINALIZED: return 'Finalized';
    case INVOICE_STATUS.SENT: return 'Sent';
    case INVOICE_STATUS.PAID: return 'Paid';
    case INVOICE_STATUS.PENDING: return 'Pending';
    case INVOICE_STATUS.OVERDUE: return 'Overdue';
    default: return status;
  }
};

/**
 * Returns the color associated with a given quote status.
 *
 * @param status - The status of the quote.
 * @returns The color string corresponding to the provided quote status.
 */
export const getQuoteStatusColor = (status: QuoteStatus): string => {
  switch (status) {
    case QUOTE_STATUS.DRAFT: return 'gray';
    case QUOTE_STATUS.AWAITING_RESPONSE: return 'blue';
    case QUOTE_STATUS.APPROVED: return 'purple';
    case QUOTE_STATUS.CHANGES_REQUESTED: return 'orange';
    case QUOTE_STATUS.PENDING_ASSIGNMENT: return 'red';
    case QUOTE_STATUS.EXCLUSIVE_PENDING: return 'indigo';
    case QUOTE_STATUS.ASSIGNED: return 'green';
    case QUOTE_STATUS.ARCHIVED: return 'gray';
    default: return 'gray';
  }
};

/**
 * Returns a human-readable label for a given quote status.
 *
 * @param status - The status of the quote as a `QuoteStatus` enum value.
 * @returns The corresponding label as a string. If the status does not match any known value, returns the status itself.
 */
export const getQuoteStatusLabel = (status: QuoteStatus): string => {
  switch (status) {
    case QUOTE_STATUS.DRAFT: return 'Draft';
    case QUOTE_STATUS.AWAITING_RESPONSE: return 'Awaiting Response';
    case QUOTE_STATUS.APPROVED: return 'Approved';
    case QUOTE_STATUS.CHANGES_REQUESTED: return 'Changes Requested';
    case QUOTE_STATUS.PENDING_ASSIGNMENT: return 'Pending Assignment';
    case QUOTE_STATUS.EXCLUSIVE_PENDING: return 'Exclusive Pending';
    case QUOTE_STATUS.ASSIGNED: return 'Assigned';
    case QUOTE_STATUS.ARCHIVED: return 'Archived';
    default: return status;
  }
};

/**
 * Determines if a transition from one job status to another is valid.
 *
 * @param from - The current job status.
 * @param to - The desired job status to transition to.
 * @returns `true` if the transition from `from` to `to` is allowed, otherwise `false`.
 *
 * @remarks
 * The valid transitions are defined as follows:
 * - REQUESTED → CONFIRMED, CANCELLED
 * - CONFIRMED → EN_ROUTE, IN_PROGRESS, CANCELLED
 * - EN_ROUTE → IN_PROGRESS, CANCELLED
 * - IN_PROGRESS → COMPLETED, CANCELLED
 * - COMPLETED → INVOICED
 * - CANCELLED and INVOICED are terminal states with no valid transitions.
 */
export const isValidJobStatusTransition = (from: JobStatus, to: JobStatus): boolean => {
  const validTransitions: Record<JobStatus, JobStatus[]> = {
    [JOB_STATUS.PENDING]: [JOB_STATUS.REQUESTED, JOB_STATUS.CANCELLED],
    [JOB_STATUS.REQUESTED]: [JOB_STATUS.CONFIRMED, JOB_STATUS.CANCELLED],
    [JOB_STATUS.CONFIRMED]: [JOB_STATUS.EN_ROUTE, JOB_STATUS.IN_PROGRESS, JOB_STATUS.CANCELLED],
    [JOB_STATUS.EN_ROUTE]: [JOB_STATUS.IN_PROGRESS, JOB_STATUS.CANCELLED],
    [JOB_STATUS.IN_PROGRESS]: [JOB_STATUS.COMPLETED, JOB_STATUS.CANCELLED],
    [JOB_STATUS.COMPLETED]: [JOB_STATUS.INVOICED],
    [JOB_STATUS.CANCELLED]: [], // Terminal state
    [JOB_STATUS.INVOICED]: [] // Terminal state
  };
  
  return validTransitions[from]?.includes(to) ?? false;
};

/**
 * Determines if a transition between two request statuses is valid.
 *
 * @param from - The current status of the request.
 * @param to - The desired status to transition to.
 * @returns `true` if the transition from `from` to `to` is allowed, otherwise `false`.
 *
 * Valid transitions:
 * - PENDING → ASSESSED, CANCELLED
 * - ASSESSED → QUOTED, CANCELLED
 * - QUOTED → EXPIRED, CANCELLED
 * - EXPIRED → QUOTED, CANCELLED
 * - CANCELLED → (no transitions, terminal state)
 */
export const isValidRequestStatusTransition = (from: RequestStatus, to: RequestStatus): boolean => {
  const validTransitions: Record<RequestStatus, RequestStatus[]> = {
    [REQUEST_STATUS.PENDING]: [REQUEST_STATUS.ASSESSED, REQUEST_STATUS.CANCELLED],
    [REQUEST_STATUS.ASSESSED]: [REQUEST_STATUS.QUOTED, REQUEST_STATUS.CANCELLED],
    [REQUEST_STATUS.QUOTED]: [REQUEST_STATUS.EXPIRED, REQUEST_STATUS.CANCELLED],
    [REQUEST_STATUS.EXPIRED]: [REQUEST_STATUS.QUOTED, REQUEST_STATUS.CANCELLED],
    [REQUEST_STATUS.CANCELLED]: [] // Terminal state
  };
  
  return validTransitions[from]?.includes(to) ?? false;
};

/**
 * Determines if a transition between two invoice statuses is valid.
 *
 * @param from - The current invoice status.
 * @param to - The desired invoice status to transition to.
 * @returns `true` if the transition from `from` to `to` is allowed, otherwise `false`.
 *
 * Valid transitions:
 * - DRAFT → FINALIZED
 * - FINALIZED → SENT
 * - SENT → PAID, OVERDUE
 * - PENDING → PAID, OVERDUE
 * - OVERDUE → PAID
 * - PAID → (no further transitions; terminal state)
 */
export const isValidInvoiceStatusTransition = (from: InvoiceStatus, to: InvoiceStatus): boolean => {
  const validTransitions: Record<InvoiceStatus, InvoiceStatus[]> = {
    [INVOICE_STATUS.DRAFT]: [INVOICE_STATUS.FINALIZED],
    [INVOICE_STATUS.FINALIZED]: [INVOICE_STATUS.SENT],
    [INVOICE_STATUS.SENT]: [INVOICE_STATUS.PAID, INVOICE_STATUS.OVERDUE],
    [INVOICE_STATUS.PENDING]: [INVOICE_STATUS.PAID, INVOICE_STATUS.OVERDUE],
    [INVOICE_STATUS.OVERDUE]: [INVOICE_STATUS.PAID],
    [INVOICE_STATUS.PAID]: [] // Terminal state
  };
  
  return validTransitions[from]?.includes(to) ?? false;
};
